﻿import React, { useMemo, useRef, useState } from 'react';
import './tirefinder.css';

import SearchResultItem from '../SearchResultItem';
import TireResultsGrid from './TireResultsGrid';

const generateOptions = (start, end, step = 5) => {
    const options = [];
    for (let i = start; i <= end; i += step) options.push(i);
    return options;
};

const widthOptions = generateOptions(145, 355, 10);
const aspectOptions = generateOptions(25, 95, 5);
const rimOptions = generateOptions(13, 24, 1);

const calculateTireDimensions = (width, aspect, rim) => {
    const sidewallHeight = (width * aspect) / 100;
    const rimDiameterMM = rim * 25.4;
    const totalDiameterMM = rimDiameterMM + (2 * sidewallHeight);
    return { sidewallHeight, rimDiameterMM, totalDiameterMM };
};

const lastindeksInfo = 'Tallkode som indikerer hvor stor belastning dekket er godkjent for av fabrikant. Må minst være like høy som akselens tillatte totalvekt delt på 2 (4 ved tvillinghjul).';
const hastighetsindeksInfo = 'Bokstavkode som indikerer hvilken maksimal hastighet dekket er godkjent for av fabrikant. Må minst være like høy som kjøretøyets maksimalhastighet.';
const innpressInfo = 'Avstanden fra flaten på felg som har kontakt med akselen på bilen til senter felg sett i lengderetning. Denne avstanden er angitt av fabrikant av kjøretøyet og vil angi sporvidden på kjøretøyet. Avstanden kan fravikes med +/- 7,5 mm på hver side, totalt 15 mm, uten videre godkjenning. Innpress er ofte betegnet ET';

// title gives desktop hover a tooltip for free; onClick/tap reveals the same text inline,
// since native title tooltips don't show on touch devices
const InfoButton = ({ infoKey, text, openInfoKey, setOpenInfoKey }) => (
    <button
        type="button"
        className="tire-info-btn"
        title={text}
        aria-label="Mer informasjon"
        aria-expanded={openInfoKey === infoKey}
        onClick={() => setOpenInfoKey(openInfoKey === infoKey ? null : infoKey)}
    >
        <span className="glyphicon glyphicon-info-sign"></span>
    </button>
);

const TireFinder = ({ tireData, tireRimSize, tireWidth, tireAspect, inputregno, firmanavn }) => {
    const [width, setWidth] = useState(tireWidth || '');
    const [aspect, setAspect] = useState(tireAspect || '');
    const [rim, setRim] = useState(tireRimSize || '');
    const [regno, setRegNo] = useState(inputregno || '');
    const [tireType, setTireType] = useState("sommer");
    const [resultsByRim, setResultsByRim] = useState({});
    const [openRim, setOpenRim] = useState(null);
    const [resultsView, setResultsView] = useState("card");
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState("");
    
    const [regnoLookupLoading, setRegnoLookupLoading] = useState(false);
    const [regnoLookupResult, setRegnoLookupResult] = useState(null);
    const [regnoLookupError, setRegnoLookupError] = useState(null);
    const [regnoLookupInfo, setRegnoLookupInfo] = useState(null);
    const [regnoLookupDimensions, setRegnoLookupDimensions] = useState([]);
    const [shareLink, setShareLink] = useState(null);
    const [vegvesenData, setVegvesenData] = useState(null);
    const [openInfoKey, setOpenInfoKey] = useState(null);

    const widthInputRef = useRef(null);
    const aspectInputRef = useRef(null);
    const rimInputRef = useRef(null);

    const tireDataByPartId = useMemo(
        () => new Map((tireData || []).map(t => [t.PartId, t])),
        [tireData]
    );

    // Parse URL query params on mount and restore state
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlWidth = params.get('width');
        const urlAspect = params.get('aspect');
        const urlRim = params.get('rim');
        const urlTireType = params.get('tireType');

        if (urlWidth && /^\d{3}$/.test(urlWidth)) {
            setWidth(urlWidth);
        }
        if (urlAspect && /^\d{2}$/.test(urlAspect)) {
            setAspect(urlAspect);
        }
        if (urlRim && /^\d{2}$/.test(urlRim)) {
            setRim(urlRim);
        }
        if (urlTireType && ['sommer', 'vinterPigg', 'vinterPiggfri'].includes(urlTireType)) {
            setTireType(urlTireType);
        }
    }, []);

    // Sync dimension changes to URL
    React.useEffect(() => {
        updateUrlParams(width, aspect, rim, tireType);
    }, [width, aspect, rim, tireType]);

    const updateUrlParams = (w, a, r, type) => {
        const params = new URLSearchParams();
        if (w && /^\d{3}$/.test(w)) params.set('width', w);
        if (a && /^\d{2}$/.test(a)) params.set('aspect', a);
        if (r && /^\d{2}$/.test(r)) params.set('rim', r);
        if (type && ['sommer', 'vinterPigg', 'vinterPiggfri'].includes(type)) {
            params.set('tireType', type);
        }
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({ path: newUrl }, '', newUrl);
    };

    const getShareLink = () => {
        const params = new URLSearchParams();
        if (width && /^\d{3}$/.test(width)) params.set('width', width);
        if (aspect && /^\d{2}$/.test(aspect)) params.set('aspect', aspect);
        if (rim && /^\d{2}$/.test(rim)) params.set('rim', rim);
        if (tireType && ['sommer', 'vinterPigg', 'vinterPiggfri'].includes(tireType)) {
            params.set('tireType', tireType);
        }
        return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    };

    const setDimensionValues = (newWidth, newAspect, newRim) => {
        setWidth(String(newWidth || ''));
        setAspect(String(newAspect || ''));
        setRim(String(newRim || ''));
        updateUrlParams(String(newWidth || ''), String(newAspect || ''), String(newRim || ''), tireType);
    };

    const handleSegmentChange = (value, maxLength, setter, nextRef) => {
        const numericValue = (value || '').replace(/\D/g, '').slice(0, maxLength);
        setter(numericValue);
        setVegvesenData(null); // manual edit invalidates the Vegvesen data tied to the previously applied dimension
        if (numericValue.length === maxLength && nextRef && nextRef.current) {
            nextRef.current.focus();
            nextRef.current.select();
        }
    };

    const handleBackspace = (event, value, prevRef) => {
        if (event.key === 'Backspace' && !value && prevRef && prevRef.current) {
            prevRef.current.focus();
            prevRef.current.select();
        }
    };

    const allDealers = Array.from(
        new Set(
            Object.values(resultsByRim)
                .flat()
                .map(item => item.DealerName)
        )
    ).sort();

    const handleDealerChange = (e) => {
        setSelectedDealer(e.target.value);
    };

    const validRegNo = () => regno && regno.length > 6 && regno.length < 9;

    const validDimensions = () => parseInt(width) > 0 && parseInt(aspect) > 0 && parseInt(rim) > 0;

    const enableSearch = (validRegNo() || validDimensions()) && !isLoading;

    const computedDimensions = useMemo(
        () => (validDimensions() ? calculateTireDimensions(parseInt(width), parseInt(aspect), parseInt(rim)) : null),
        [width, aspect, rim]
    );

    const handleRegnoLookup = async () => {
        const normalized = (regno || '').trim().toUpperCase().replace(/\s+/g, '');
        
        // Regex validation for registration number format
        if (!/^[A-Z]{2}\d{5}$/.test(normalized)) {
            setRegnoLookupError('Registreringsnummeret må være på format AB12345');
            setRegnoLookupDimensions([]);
            return;
        }

        setRegnoLookupLoading(true);
        setRegnoLookupError(null);
        setRegnoLookupInfo(null);
        setRegnoLookupResult(null);
        setRegnoLookupDimensions([]);
        setVegvesenData(null);

        try {
            const response = await fetch('/api/tyre/GetTyreSizeForCar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ regnr: normalized })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                setRegnoLookupError('Fant ikke dekkdimensjoner for dette registreringsnummeret');
                return;
            }

            const dimensions = Array.isArray(data.dimensions) ? data.dimensions : [];
            if (!dimensions.length) {
                setRegnoLookupError('Ingen dekkdimensjoner funnet for dette kjøretøyet');
                return;
            }

            // Store vehicle info and all dimensions
            setRegnoLookupResult({
                vehicleDescription: data.vehicleDescription,
                dimensions: dimensions
            });
            setRegnoLookupDimensions(dimensions);

            // Auto-apply if only one option
            if (dimensions.length === 1) {
                setDimensionValues(dimensions[0].width, dimensions[0].aspectRatio, dimensions[0].diameter);
                setVegvesenData(dimensions[0]);
                setRegnoLookupInfo('Dimensjon hentet automatisk fra kjøretøydata.');
                return;
            }

            // Multiple options - show info and chips
            if (dimensions.length > 1) {
                setRegnoLookupInfo('Fant flere dekkdimensjoner. Velg en av alternativene under.');
            }
        } catch (error) {
            console.error('Feil ved registreringsnummeroppslagg:', error);
            setRegnoLookupError('Feil ved oppslag. Prøv igjen senere.');
        } finally {
            setRegnoLookupLoading(false);
        }
    };

    const applyDimensionFromLookup = (dim) => {
        setDimensionValues(dim.width, dim.aspectRatio, dim.diameter);
        setVegvesenData(dim);
        setRegnoLookupResult(null);
        setRegnoLookupDimensions([]);
        setRegnoLookupInfo(null);
    };

    const handleSearch = async () => {
        setIsLoading(true);

        let searchWidth = width;
        let searchAspect = aspect;
        let searchRim = rim;

        if (validRegNo()) {
            const response = await fetch("/api/tyre/GetTyreSizeForCar", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ regnr: regno })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.dimensions && data.dimensions.length) {
                    const first = data.dimensions[0];
                    searchWidth = first.width;
                    searchAspect = first.aspectRatio;
                    searchRim = first.diameter;
                } else {
                    setIsLoading(false);
                    setSearchPerformed(true);
                    return;
                }
            } else {
                setIsLoading(false);
                setSearchPerformed(true);
                return;
            }
        } else if (!(searchWidth && searchAspect && searchRim)) {
            setIsLoading(false);
            return;
        }

        const widthNum = parseInt(searchWidth);
        const aspectNum = parseInt(searchAspect);
        const rimNum = parseInt(searchRim);
        const originalDimensions = calculateTireDimensions(widthNum, aspectNum, rimNum);

        const modelId = { sommer: 8050, vinterPigg: 8055, vinterPiggfri: 8056 }[tireType];

        try {
            const response = await fetch(`/api/search/index/?brand_id=1000&model_id=${modelId}&firmanavn=${firmanavn || 'nbf'}&pagesize=1000&tireWidth=${widthNum}&tireAspect=${aspectNum}&tireRim=${rimNum}`);
            const data = await response.json();
            const searchItems = data.SearchResults.Results;

            const enriched = searchItems
                .map(item => {
                    const matched = tireDataByPartId.get(item.Part_Id);
                    if (matched) {
                        const originalCircumference = originalDimensions.totalDiameterMM * Math.PI;
                        const matchedCircumference = matched.TotalDiameterMM * Math.PI;
                        const deviation = ((matchedCircumference - originalCircumference) / originalCircumference) * 100;
                        return { ...item, ...matched, deviation };
                    }
                    return null;
                })
                .filter(Boolean);

            const compatible = enriched.filter(tire => Math.abs(tire.deviation) <= 5);
            const sortedCompatible = compatible.sort((a, b) => Math.abs(a.deviation) - Math.abs(b.deviation));

            const grouped = sortedCompatible.reduce((acc, tire) => {
                const rimKey = tire.RimSize;
                if (!acc[rimKey]) acc[rimKey] = [];
                acc[rimKey].push(tire);
                return acc;
            }, {});

            setResultsByRim(grouped);
            const groupedRims = Object.keys(grouped).map(Number).sort((a, b) => a - b);
            setOpenRim(groupedRims.includes(rimNum) ? rimNum : (groupedRims[0] ?? null));
            setWidth(searchWidth);
            setAspect(searchAspect);
            setRim(searchRim);
            setSearchPerformed(true);
        } catch (error) {
            console.error('Feil under henting av dekk:', error);
            setSearchPerformed(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleregChange = (e) => {
        setRegNo(e.target.value.toUpperCase());
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && enableSearch) {
            handleSearch();
        }
    };

    const rimOrder = Object.keys(resultsByRim)
        .map(Number)
        .sort((a, b) => {
            if (a === parseInt(rim)) return -1;
            if (b === parseInt(rim)) return 1;
            return a - b;
        });

    return (
        <>
        <div className="container tire-finder-container">
            <div className="border-start border-dark ps-3 mb-4" style={{ borderLeftWidth: '4px', backgroundColor: '#f8f9fa', padding: '12px 16px', fontSize: '0.9rem', lineHeight: '1.6', color: '#333' }}>
                <strong>Juridisk ansvarsfraskrivelse:</strong> Vi viser kun alternative dekkdimensjoner som er innenfor tillatt avvik per norsk lovgivning.
                Det er likevel ditt fulle ansvar som bilfører å sikre at dekkene som monteres er lovlige og i henhold til kjøretøyets typegodkjenning og gjeldende forskrifter.
            </div>

            <div className="tire-search-card">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            {/* Registration number lookup - Primary input method */}
            <div className="mb-2">
                <div>
                    <h4>Slå opp på registreringsnummer</h4>
                    <div className="mb-2" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="AB 12345"
                            value={regno}
                            onChange={handleregChange}
                            onKeyDown={e => e.key === 'Enter' && handleRegnoLookup()}
                            maxLength={8}
                            style={{ maxWidth: '100px', height: '32px' }}
                        />
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={handleRegnoLookup}
                            disabled={regnoLookupLoading || !regno.trim()}
                            style={{ height: '32px', whiteSpace: 'nowrap' }}
                        >
                            {regnoLookupLoading ? '...' : (<><span className="glyphicon glyphicon-search" style={{ marginRight: '6px' }}></span>Slå opp</>)}
                        </button>
                    </div>
                    {regnoLookupInfo && (
                        <div className="alert alert-info alert-sm small" role="alert">
                            {regnoLookupInfo}
                        </div>
                    )}
                    {regnoLookupError && (
                        <div className="alert alert-danger alert-sm small" role="alert">
                            {regnoLookupError}
                        </div>
                    )}
                    {regnoLookupResult && regnoLookupDimensions.length > 0 && (
                        <div className="border p-2 rounded mt-2">
                            <small className="text-muted d-block mb-2">
                                <strong>Dimensjoner funnet</strong>
                            </small>
                            <div className="d-flex flex-wrap gap-1">
                                {regnoLookupDimensions.map((dim, i) => (
                                    <button
                                        key={i}
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => applyDimensionFromLookup(dim)}
                                    >
                                        Velg {dim.raw}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Quick-entry dimensional inputs - Alternative method */}
            <div className="tire-search-divider">eller</div>

            <div className="mb-2">
                <div>
                    <h4>Fyll inn dekkdimensjon manuelt</h4>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                        <div style={{ width: '64px' }}>
                            <label className="form-label small mb-1" htmlFor="input-bredde">Bredde</label>
                            <input
                                id="input-bredde"
                                ref={widthInputRef}
                                type="text"
                                className="form-control"
                                inputMode="numeric"
                                maxLength={3}
                                placeholder="205"
                                value={width || ''}
                                onChange={e => handleSegmentChange(e.target.value, 3, setWidth, aspectInputRef)}
                                onKeyDown={e => handleBackspace(e, width, null)}
                            />
                        </div>
                        <span className="text-muted" style={{ marginBottom: '8px' }}>/</span>
                        <div style={{ width: '54px' }}>
                            <label className="form-label small mb-1" htmlFor="input-profil">Profil</label>
                            <input
                                id="input-profil"
                                ref={aspectInputRef}
                                type="text"
                                className="form-control"
                                inputMode="numeric"
                                maxLength={2}
                                placeholder="55"
                                value={aspect || ''}
                                onChange={e => handleSegmentChange(e.target.value, 2, setAspect, rimInputRef)}
                                onKeyDown={e => handleBackspace(e, aspect, widthInputRef)}
                            />
                        </div>
                        <span className="text-muted" style={{ marginBottom: '8px' }}>R</span>
                        <div style={{ width: '54px' }}>
                            <label className="form-label small mb-1" htmlFor="input-felg">Felg</label>
                            <input
                                id="input-felg"
                                ref={rimInputRef}
                                type="text"
                                className="form-control"
                                inputMode="numeric"
                                maxLength={2}
                                placeholder="16"
                                value={rim || ''}
                                onChange={e => handleSegmentChange(e.target.value, 2, setRim)}
                                onKeyDown={e => handleBackspace(e, rim, aspectInputRef)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tyre type and search button */}
            <div className="mb-4">
                <div>
                    <div className="form-group">
                        <label className="form-label">Dekkstype</label>
                        <div>
                            {[
                                { value: 'sommer', label: 'Sommerdekk' },
                                { value: 'vinterPiggfri', label: 'Vinterdekk piggfritt' },
                                { value: 'vinterPigg', label: 'Vinterdekk med pigg' }
                            ].map(opt => (
                                <div key={opt.value} className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        id={`tire-${opt.value}`}
                                        value={opt.value}
                                        checked={tireType === opt.value}
                                        onChange={e => setTireType(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor={`tire-${opt.value}`}>
                                        {opt.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button 
                        className="btn btn-primary mt-3"
                        onClick={handleSearch}
                        disabled={!enableSearch}
                    >
                        {isLoading ? (
                            <>
                                <span style={{
                                    display: 'inline-block',
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid rgba(255,255,255,0.4)',
                                    borderTopColor: 'white',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                    marginRight: '8px',
                                    verticalAlign: 'middle'
                                }} />
                                Søker etter dekk...
                            </>
                        ) : (<><span className="glyphicon glyphicon-search" style={{ marginRight: '6px' }}></span>Søk etter dekk</>)}
                    </button>
                </div>
            </div>

            </div>{/* end centered form wrapper */}
            </div>{/* end tire-search-card */}

            {computedDimensions && (
                <div className="alert alert-info">
                    <h5>Beregnede dimensjoner for {width}/{aspect} R{rim}</h5>
                    <ul className="mb-0">
                        <li>Sidehøyde: {computedDimensions.sidewallHeight.toFixed(2)} mm</li>
                        <li>Felgdiameter i mm: {computedDimensions.rimDiameterMM.toFixed(2)} mm</li>
                        <li>Total diameter: {computedDimensions.totalDiameterMM.toFixed(2)} mm</li>
                    </ul>

                    {vegvesenData && (vegvesenData.loadIndexFront || vegvesenData.loadIndexRear || vegvesenData.speedIndex || vegvesenData.offsetFront || vegvesenData.offsetRear) && (
                        <div className="mt-2 pt-2 border-top">
                            <strong className="d-block mb-1">Data fra Statens Vegvesen</strong>
                            <ul className="mb-0">
                                <li>
                                    Lastindeks foran: {vegvesenData.loadIndexFront || '-'}{' '}
                                    <InfoButton infoKey="lastindeksForan" text={lastindeksInfo} openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey} />
                                    {openInfoKey === 'lastindeksForan' && <div className="tire-info-popover">{lastindeksInfo}</div>}
                                </li>
                                <li>
                                    Lastindeks bak: {vegvesenData.loadIndexRear || '-'}{' '}
                                    <InfoButton infoKey="lastindeksBak" text={lastindeksInfo} openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey} />
                                    {openInfoKey === 'lastindeksBak' && <div className="tire-info-popover">{lastindeksInfo}</div>}
                                </li>
                                <li>
                                    Hastighetsindeks: {vegvesenData.speedIndex || '-'}{' '}
                                    <InfoButton infoKey="hastighetsindeks" text={hastighetsindeksInfo} openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey} />
                                    {openInfoKey === 'hastighetsindeks' && <div className="tire-info-popover">{hastighetsindeksInfo}</div>}
                                </li>
                                <li>
                                    Innpress foran: {vegvesenData.offsetFront || '-'} bak: {vegvesenData.offsetRear || '-'}{' '}
                                    <InfoButton infoKey="innpress" text={innpressInfo} openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey} />
                                    {openInfoKey === 'innpress' && <div className="tire-info-popover">{innpressInfo}</div>}
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {rimOrder.length === 0 && searchPerformed && (
                <div className="alert alert-info text-center">
                    Ingen dekk funnet. Prøv et annet registreringsnummer eller dimensjon.
                </div>
            )}
        </div>

        {rimOrder.length > 0 && (
            <div className="tire-finder-container tire-finder-results results">
                <h3 className="text-center">Tilgjengelige dekk</h3>

                <div className="mb-3">
                    <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => {
                            const link = getShareLink();
                            navigator.clipboard.writeText(link);
                            setShareLink(link);
                            setTimeout(() => setShareLink(null), 3000);
                        }}
                    >
                        <span className={`glyphicon ${shareLink ? 'glyphicon-ok' : 'glyphicon-share-alt'}`} style={{ marginRight: '6px' }}></span>
                        {shareLink ? 'Lenke kopiert' : 'Del søk'}
                    </button>
                </div>

                <div className="form-group">
                    <label>Visningsmodus:</label>
                    <div>
                        <label className="me-3">
                            <input
                                type="radio"
                                value="card"
                                checked={resultsView === "card"}
                                onChange={() => setResultsView("card")}
                            /> Kortvisning
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="grid"
                                checked={resultsView === "grid"}
                                onChange={() => setResultsView("grid")}
                            /> Tabellvisning
                        </label>
                    </div>
                </div>

                {allDealers.length > 1 && (
                    <div className="form-group">
                        <label>Filtrer på forhandler</label>
                        <select className="form-control" value={selectedDealer} onChange={handleDealerChange}>
                            <option value="">Alle forhandlere</option>
                            {allDealers.map(dealer => (
                                <option key={dealer} value={dealer}>{dealer}</option>
                            ))}
                        </select>
                    </div>
                )}

                {resultsView === "card" ? (
                    <div className="panel-group tire-rim-accordion">
                        {rimOrder.map(rimSize => {
                            const isSelected = parseInt(rimSize) === parseInt(rim);
                            const isOpen = openRim === rimSize;
                            const items = resultsByRim[rimSize].filter(item => !selectedDealer || item.DealerName === selectedDealer);
                            return (
                                <div key={rimSize} className="panel panel-default">
                                    <div className="panel-heading" role="tab">
                                        <h4 className="panel-title">
                                            <button
                                                type="button"
                                                className="tire-rim-toggle"
                                                aria-expanded={isOpen}
                                                aria-controls={`tire-rim-panel-${rimSize}`}
                                                onClick={() => setOpenRim(isOpen ? null : rimSize)}
                                            >
                                                <span className={`glyphicon ${isOpen ? 'glyphicon-chevron-down' : 'glyphicon-chevron-right'}`} style={{ marginRight: '8px' }}></span>
                                                Felgstørrelse: {rimSize}" {isSelected && <strong>(Valgt)</strong>}
                                                <span className="badge pull-right">{items.length}</span>
                                            </button>
                                        </h4>
                                    </div>
                                    <div
                                        id={`tire-rim-panel-${rimSize}`}
                                        className={`panel-collapse collapse${isOpen ? ' in' : ''}`}
                                        role="tabpanel"
                                    >
                                        <div className="list-group">
                                            {items.map(item => (
                                                <div key={item.StorageId} className="list-group-item px-0">
                                                    <div className="text-muted mb-1" style={{ fontSize: '0.95rem', fontWeight: 600 }}>Avvik: {item.deviation.toFixed(2)}%</div>
                                                    <div className="partDetailsSearchResults">
                                                        <SearchResultItem id={item.StorageId} data={item} firmanavn={firmanavn || 'nbf'} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <TireResultsGrid
                        items={rimOrder.flatMap(rimSize =>
                            resultsByRim[rimSize].filter(item => !selectedDealer || item.DealerName === selectedDealer)
                        )}
                        firmanavn={firmanavn}
                    />
                )}
            </div>
        )}
        </>
    );
};

export default TireFinder;
